import type { BackfillProgress, Job } from "@malfak/contracts";
import { TriangleAlert } from "lucide-react";
import { Num } from "@/components/ui/misc";
import { fmtNumber } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { backfillLine, backfillProgressOf } from "./model";

/** Stage counters of an initial-import job, under its progress in the jobs UI (renders nothing for other kinds). */
export function BackfillDetails({ job, compact = false, className }: { job: Pick<Job, "kind" | "result">; compact?: boolean; className?: string }) {
  const { t } = useT();
  const p = backfillProgressOf(job);
  if (!p) return null;
  const missing = p.commentsMissing ?? 0;
  if (compact) {
    return (
      <span className={className} data-testid="backfill-line">
        <Num>{backfillLine(p, t)}</Num>
        {missing > 0 && (
          <span className="text-warn" data-testid="backfill-missing-short">
            {" · "}
            <Num>{t("oauth.backfill.missing.short", { n: fmtNumber(missing) })}</Num>
          </span>
        )}
      </span>
    );
  }
  return (
    <div className={className} data-testid="backfill-line">
      <p className="text-[12px] font-medium text-ink-2">
        <Num>{backfillLine(p, t)}</Num>
      </p>
      <p className="mt-0.5 text-[11.5px] text-ink-3">
        {t(`oauth.backfill.stage.${p.stage}`)}
        {p.rateLimitedWaits > 0 && <> · {t("oauth.backfill.rateLimited", { count: p.rateLimitedWaits })}</>}
      </p>
      {missing > 0 && <BackfillGaps p={p} />}
    </div>
  );
}

/** QA F8: the import finished with a partial result: say how many comments are missing and why (never a plain success). */
function BackfillGaps({ p }: { p: BackfillProgress }) {
  const { t } = useT();
  const gaps = p.gaps ?? [];
  const sum = (reason: "not_returned" | "api_error") => gaps.filter((g) => g.reason === reason).reduce((a, g) => a + g.missing, 0);
  const notReturned = sum("not_returned");
  const apiError = sum("api_error");
  const detail = gaps.find((g) => g.reason === "api_error" && g.detail)?.detail ?? null;
  const n = (v: number) => fmtNumber(v);
  return (
    <div role="status" className="mt-2 flex gap-2 rounded-lg border border-[color-mix(in_srgb,var(--warn)_40%,var(--line))] bg-surface-2 p-2.5 text-[12px] text-ink-2" data-testid="backfill-gaps">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-warn" aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="font-semibold text-ink">
          <Num>{t("oauth.backfill.missing.title", { n: n(p.commentsMissing ?? 0) })}</Num>
        </p>
        {notReturned > 0 && <p data-testid="backfill-gap-not_returned"><Num>{t("oauth.backfill.missing.not_returned", { n: n(notReturned) })}</Num></p>}
        {apiError > 0 && (
          <p data-testid="backfill-gap-api_error">
            <Num>{t("oauth.backfill.missing.api_error", { n: n(apiError), detail: detail ? ` (${detail})` : "" })}</Num>
          </p>
        )}
        {gaps.length > 0 && <p className="text-ink-3"><Num>{t("oauth.backfill.missing.posts", { n: n(gaps.length) })}</Num> · {t("oauth.backfill.missing.recheck")}</p>}
      </div>
    </div>
  );
}
